const workerInitializationEvents = (() => {

    let contentWorker;
    let pendingRequests = {};
    let workerInitialized = false;

    const initializeWorker = function () {
        if (workerInitialized) {
            console.log('Worker is already initialized.');
            return;
        }

        if (contentWorker) {
            try {
                contentWorker.terminate();
                console.log('Existing worker terminated before reinitializing.');
            } catch (err) {
                console.error('Error terminating existing worker:', err);
            }
        }

        contentWorker = new Worker(new URL('./contentWorker.js', import.meta.url));
       // console.log('Worker initialized.');
        workerInitialized = true;

        contentWorker.onmessage = function (event) {
            const { id, action, status, content, key, lastUpdated, error, message, data } = event.data;
            const responseType = status || action;  // Determine whether it's a response (status) or request (action)

            // Handle responses to previously sent requests
            if (status && pendingRequests[id]) {
                switch (responseType) {

                    case 'initialized':
                        console.log(`Editor initialized successfully with hash: ${message}`);
                        pendingRequests[id].resolve(`Editor initialized for key: ${key}`);
                        break;

                    case 'processed':
                        const decodedProcessedContent = new TextDecoder().decode(new Uint8Array(content));
                        pendingRequests[id].resolve(decodedProcessedContent);
                        break;

                    case 'saved':
                        pendingRequests[id].resolve(`[File] File Saved Locally, filename: ${key} at ${lastUpdated}`);
                        break;

                    case 'unchanged':  // Handle unchanged status
                        pendingRequests[id].resolve(message);
                        break;

                    case 'retrieved':
                        const decodedRetrievedContent = new TextDecoder().decode(new Uint8Array(content));
                        pendingRequests[id].resolve({ content: decodedRetrievedContent, key, lastUpdated });
                        break;

                    case 'deleted':
                        pendingRequests[id].resolve(`Content deleted for key: ${key}`);
                        break;

                    case 'not_found':
                        pendingRequests[id].resolve({ content: null, key });
                        break;

                    case 'terminated':
                        console.log('Workder terminated');
                        break;

                    case 'renamed':
                        resolve(`File renamed successfully from ${key.oldKey} to ${key.newKey}`);
                        break;

                    case 'error':
                    case 'db_error':
                        console.log(`Worker error for request ID ${id}: ${error}`);
                        pendingRequests[id].reject(`Worker error: ${error}`);
                        break;

                    default:
                        console.log(`Unexpected worker response for ID: ${id}`, event.data);
                        pendingRequests[id].reject('Unknown worker response');
                }
                delete pendingRequests[id];
            }

            // Handle requests from the worker (LocalStorage operations)
            else {
                switch (action) {
                    case 'saveToLocalStorage': {
                        try {
                            saveContentWithLimitToLocalStorage(data.filename, data.content);
                            contentWorker.postMessage({
                                id,
                                action: 'saveToLocalStorageSuccess',
                                data: {
                                    key: `editor-${data.filename}`,
                                    lastUpdated: new Date().toISOString()
                                }
                            });
                        } catch (err) {
                            contentWorker.postMessage({
                                id,
                                action: 'saveToLocalStorageError',
                                data: {
                                    key: `editor-${data.filename}`,
                                    message: `Failed to save content to LocalStorage: ${err.message}`
                                }
                            });
                        }
                        break;
                    }

                    case 'getFromLocalStorage': {
                        try {
                            const retrievedContent = getLocalStorageSavedContent(data.filename);
                            if (retrievedContent) {
                                contentWorker.postMessage({
                                    id,
                                    action: 'getFromLocalStorageSuccess',
                                    data: {
                                        key: `editor-${data.filename}`,
                                        content: retrievedContent,
                                        lastUpdated: new Date().toISOString()
                                    }
                                });
                            } else {
                                contentWorker.postMessage({
                                    id,
                                    action: 'getFromLocalStorageNotFound',
                                    data: { key: `editor-${data.filename}` }
                                });
                            }
                        } catch (err) {
                            contentWorker.postMessage({
                                id,
                                action: 'getFromLocalStorageError',
                                data: {
                                    key: `editor-${data.filename}`,
                                    message: `Error retrieving from LocalStorage: ${err.message}`
                                }
                            });
                        }
                        break;
                    }

                    case 'deleteFromLocalStorage': {
                        try {
                            const deletionSuccess = deleteLocalStorageSavedContent(data.filename);
                            if (deletionSuccess) {
                                contentWorker.postMessage({
                                    id,
                                    action: 'deleteFromLocalStorageSuccess',
                                    data: { key: `editor-${data.filename}` }
                                });
                            } else {
                                contentWorker.postMessage({
                                    id,
                                    action: 'deleteFromLocalStorageNotFound',
                                    data: { key: `editor-${data.filename}` }
                                });
                            }
                        } catch (err) {
                            contentWorker.postMessage({
                                id,
                                action: 'deleteFromLocalStorageError',
                                data: {
                                    key: `editor-${data.filename}`,
                                    message: `Error deleting from LocalStorage: ${err.message}`
                                }
                            });
                        }
                        break;
                    }

                    default:
                        console.log(`No pending request found for ID: ${id}`);
                }
            }
        };

        // Handle worker errors
        contentWorker.onerror = function (error) {
            console.log(`Worker runtime error: ${error.message}`);
            rejectAllPendingRequests(`Worker runtime error: ${error.message}`);
            resetWorkerState();
        };
    };

    const getContentWorker = () => contentWorker;
    const getPendingRequests = () => pendingRequests;

    const rejectAllPendingRequests = function (message) {
        Object.values(pendingRequests).forEach(({ reject }) => {
            if (typeof reject === 'function') {
                reject(message);
            }
        });
        pendingRequests = {};
    };


    const resetWorkerState = () => {
        contentWorker = null;
        workerInitialized = false;
        console.log('Worker state reset.');
    };

    const terminateWorker = () => {
        const currentMessageId = getMessageId();
        contentWorker.postMessage({
            id: currentMessageId,
            action: 'terminateWorker'
        });
    };

    // This is for testing purposes; remove this function and replace the call to saveContentWithLimitToLocalStorage
    const saveContentWithLimitToLocalStorage = (currentFileName, content) => {
        const prefix = 'editor-';
        const timestamp = Date.now();
        const newKey = `${prefix}${currentFileName}-${timestamp}`;

        // Step 1: Retrieve all localStorage keys at once
        const allKeys = Array.from({ length: localStorage.length }, (_, i) => localStorage.key(i));

        // Step 2: Filter out editor-related keys
        const editorKeys = allKeys.filter(key => key.startsWith(prefix));

        // Step 3: Identify if there's an existing key for the currentFileName
        const existingFileKey = editorKeys.find(key => key.startsWith(`${prefix}${currentFileName}-`));

        // Step 4: Delete existing key for the current file (if found)
        if (existingFileKey) {
            deleteLocalStorageItem(existingFileKey);
            console.log(`Deleted existing key for ${currentFileName}: ${existingFileKey}`);
        }

        // Step 5: If more than 5 keys exist (since we'll add one more), remove the oldest
        if (editorKeys.length >= 5) {
            // Extract timestamps and sort keys by oldest first
            const keysWithTimestamps = editorKeys.map(key => {
                const timestampMatch = key.match(/-(\d+)$/);
                return { key, timestamp: timestampMatch ? parseInt(timestampMatch[1], 10) : 0 };
            });

            // Sort by timestamp
            keysWithTimestamps.sort((a, b) => a.timestamp - b.timestamp);

            // Remove the oldest key
            const oldestKey = keysWithTimestamps[0].key;
            deleteLocalStorageItem(oldestKey);
            console.log(`Deleted oldest key: ${oldestKey}`);
        }


        setLocalStorageItem(newKey, arrayBufferToBase64(content));
        console.log(`Saved content under key: ${newKey}`);



    };

    const getLocalStorageSavedContent = (currentFileName) => {
        console.log('Calling getLocalStorageSavedContent with file name', currentFileName);
        const prefix = 'editor-' + currentFileName;
        try {
            // Iterate over all localStorage keys to find the first match
            for (let i = 0; i < localStorage.length; i++) {
                const key = localStorage.key(i);

                // Return the first key that matches the prefix
                if (key.startsWith(prefix + '-')) {
                    return base64ToArrayBuffer(localStorage.getItem(key));
                }
            }

            console.log(`No entry found for ${prefix}`);
            return null; // If no matching key is found
        } catch (error) {
            console.log('Error accessing localStorage:', error);
            return null;
        }
    };


    function arrayBufferToBase64(buffer) {
        let binary = '';
        const bytes = new Uint8Array(buffer);
        for (let i = 0; i < bytes.byteLength; i++) {
            binary += String.fromCharCode(bytes[i]);
        }
        return btoa(binary); // Convert to Base64
    }

    function base64ToArrayBuffer(base64) {
        const binary = atob(base64);
        const len = binary.length;
        const buffer = new Uint8Array(len);
        for (let i = 0; i < len; i++) {
            buffer[i] = binary.charCodeAt(i);
        }
        return buffer.buffer; // Convert back to ArrayBuffer
    }


    const deleteLocalStorageSavedContent = (currentFileName) => {
        console.log ('Delete from local storage');
        const prefix = 'editor-' + currentFileName + ".html";
        try {
            // Iterate over all localStorage keys to find the first match
            for (let i = 0; i < localStorage.length; i++) {
                const key = localStorage.key(i);

                // Delete the first key that matches the prefix
                if (key.startsWith(prefix + '-')) {
                    localStorage.removeItem(key);
                    console.log(`Successfully deleted key: ${key}`);
                    return true; // Deletion successful
                }
            }

            console.log(`No entry found for ${prefix}`);
            return false; // If no matching key is found
        } catch (error) {
            console.log('Error deleting from localStorage:', error);
            return false;
        }
    };



    const unload = () => {
        if (contentWorker) {
            terminateWorker();
            contentWorker.terminate();

        }

        console.log('Pending Requests:', pendingRequests);

        // Ensure all pending promises are cleared
        Object.values(pendingRequests).forEach(({ reject }) => {
            reject('Page unloaded before processing was completed.');
        });
        pendingRequests = {};
        workerInitialized = false;
    }

    return { initializeWorker, unload, getPendingRequests, getContentWorker };

})();

export default workerInitializationEvents;
